import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';
import { Toast } from 'src/common/components';

import {
  createAccount,
  CreateAccountPayload,
  createSharedAccount,
  CreateSharedAccountPayload,
  createSharedAccountUsers,
  CreateSharedAccountUsersPayload,
  createSharedModule,
  CreateSharedModulePayload,
  createPlaybackAudio,
  configureSharedIvr,
  ConfigureIvrPayload,
  IvrOptionAudio,
  fetchFreeswitchStackCredentials,
  FreeswitchStackConflict,
  provisionFreeswitchStack,
  StackPollAbortedError,
  waitForFreeswitchStack,
  fetchWizardReadyGroups,
  FreeSwitchGroup,
  createResolutionStrategy,
  CreateResolutionStrategyPayload,
  createUnavailableReason,
  updateCosmosPhoneSettings,
} from '../api';
import { importPlaybackFromUrl } from '../api/playbackAudios';
import WizardNav from '../components/createAccountWizard/WizardNav';
import WizardSidebar from '../components/createAccountWizard/WizardSidebar';
import {
  AccountStep,
  AudiosStep,
  CosmosSearchStep,
  FreeSwitchTypeStep,
  InfrastructureStep,
  IvrStep,
  ModulesStep,
  PhoneFeaturesStep,
  QueuesStep,
  ResolutionStrategyStep,
  ScheduleStep,
  UsersStep,
} from '../components/createAccountWizard/steps';
import {
  getAudioUsages,
  WizardStepKey,
  WizardState,
  accountFieldsFromStack,
  areUnavailableReasonsValid,
  buildCosmosPhoneSettings,
  buildIvrOptionsPayload,
  buildUnavailableReasonsPayload,
  buildWizardSteps,
  createInitialWizardState,
  isIvrTreeValid,
  mockSleep,
  shouldCreateUnavailableReasons,
} from '../components/createAccountWizard/types';
import { toE164 } from '../utils';

const isPrototype = process.env.REACT_APP_ENV === 'mirage';

export const CreateAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<WizardStepKey>('cosmos-search');
  const [state, setState] = useState<WizardState>(() => createInitialWizardState(isPrototype));
  const [provisioning, setProvisioning] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [freeswitchGroups, setFreeswitchGroups] = useState<FreeSwitchGroup[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Track completed steps
  const [completedSteps, setCompletedSteps] = useState<Set<WizardStepKey>>(new Set());

  // Shared accounts always get the IVR step, since any client may want one;
  // dedicated ones only get the first steps in production.
  const steps = useMemo(
    () => buildWizardSteps(state.freeswitch_type, isPrototype),
    [state.freeswitch_type]
  );

  const mounted = useRef(true);
  const pollAbort = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      mounted.current = false;
      pollAbort.current?.abort();
    },
    []
  );

  // Fetch FreeSWITCH groups on mount
  useEffect(() => {
    const loadGroups = async (): Promise<void> => {
      setLoadingGroups(true);
      try {
        const groups = await fetchWizardReadyGroups();
        if (mounted.current) setFreeswitchGroups(groups);
      } catch {
        // Silently ignore - groups will be empty and shared option won't be available
      } finally {
        if (mounted.current) setLoadingGroups(false);
      }
    };
    void loadGroups();
  }, []);

  const patchState = useCallback((patch: Partial<WizardState>) => {
    if (!mounted.current) return;
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  const currentIdx = steps.findIndex((item) => item.key === step);
  const hasIvrStep = steps.some((item) => item.key === 'ivr');
  const isLast = currentIdx === steps.length - 1;

  const isAccountValid = (): boolean => {
    const baseValid =
      state.account_name.trim() !== '' &&
      toE164(state.phone_number) !== null &&
      toE164(state.call_rescue_number) !== null &&
      state.cosmos_account_id.trim() !== '' &&
      state.admin_username.trim() !== '' &&
      state.admin_password !== '';

    if (state.freeswitch_type === 'shared') {
      // Shared mode: group must be selected (already validated in freeswitch-type step)
      return baseValid;
    }

    // Dedicated mode: need infrastructure details
    return (
      baseValid &&
      state.instance_id.trim() !== '' &&
      state.freeswitch_uri.trim() !== '' &&
      (state.provisioned_client_name !== '' || state.pg_connection_string.trim() !== '')
    );
  };

  const stepValid = (key: WizardStepKey): boolean => {
    switch (key) {
      case 'cosmos-search':
        // Must have selected a Cosmos account
        return state.cosmos_account !== null;
      case 'freeswitch-type':
        // For shared, must have a group selected; for dedicated, always can proceed
        return state.freeswitch_type === 'dedicated' || state.selected_group !== null;
      case 'infrastructure':
        return state.infra_ready;
      case 'account':
        return isAccountValid();
      case 'users':
        // Must have at least one user AND not exceed max_users limit
        if (state.users.length === 0) return false;
        if (state.max_users !== null && state.users.length > state.max_users) return false;
        return true;
      case 'schedule':
        return state.schedule.time_slots.some((slot) => slot.is_open);
      case 'audios':
        return state.audios.length > 0;
      case 'resolution-strategies':
        // At least one strategy with at least one tag
        return (
          state.resolution_strategies.length > 0 &&
          state.resolution_strategies.every((s) => s.name.trim() && s.tags.length > 0)
        );
      case 'modules':
        return state.modules.length > 0;
      case 'ivr':
        // IVR disabled = valid; otherwise need options and ALL options must have audio
        if (!state.ivr.enabled) return true;
        if (state.use_cosmos_ivr) return true; // Cosmos IVR handles its own audio
        return isIvrTreeValid(state.ivr.options);
      case 'phone-features':
        return areUnavailableReasonsValid(state);
      case 'queues':
        return true;
      default:
        return false;
    }
  };

  const canGoNext = (): boolean => stepValid(step);

  const handleProvision = async (): Promise<void> => {
    setProvisioning(true);
    try {
      if (isPrototype) {
        await mockSleep(1200);
        const client = state.client_name.trim() || 'client';
        patchState({
          stack_name: 'freeswitch-prod-mirage',
          stack_status: 'CREATE_COMPLETE',
          ...accountFieldsFromStack(state, {
            client_name: client,
            instance_id: 'i-0123456789abcdef0',
            instance_private_ip: '10.0.0.10',
            efs_access_point_id: 'fsap-0123456789abcdef0',
            pg_database: `${client}_freeswitch_db@db.example.invalid`,
          }),
        });
        Toast.success('Datos de infraestructura de prueba generados');
        return;
      }

      const clientName = state.client_name.trim();
      let stackName = state.stack_name.trim();

      if (!stackName) {
        const created = await provisionFreeswitchStack({
          cosmos_account_uuid: state.cosmos_account_id.trim(),
          client_name: clientName,
        });
        stackName = created.stack_name;
        patchState({
          stack_name: created.stack_name,
          stack_status: created.status,
          stack_console_url: created.console_url,
        });
        Toast.success(`Despliegue iniciado: ${created.stack_name}`);
      }

      const abort = new AbortController();
      pollAbort.current = abort;

      const stack = await waitForFreeswitchStack(
        stackName,
        (update) =>
          patchState({ stack_status: update.status, stack_console_url: update.console_url }),
        { signal: abort.signal }
      );
      const credentials = await fetchFreeswitchStackCredentials(clientName, abort.signal);

      patchState(
        accountFieldsFromStack(state, {
          client_name: clientName,
          instance_id: stack.instance_id,
          instance_private_ip: stack.instance_private_ip,
          efs_access_point_id: stack.efs_access_point_id,
          pg_database: `${credentials.database}@${credentials.host}`,
        })
      );
      Toast.success('FreeSWITCH aprovisionado');
    } catch (err: unknown) {
      if (err instanceof StackPollAbortedError || !mounted.current) return;

      const axiosErr = err as {
        response?: { status?: number; data?: { detail?: string | FreeswitchStackConflict } };
        message?: string;
      };
      const detail = axiosErr?.response?.data?.detail;

      if (axiosErr?.response?.status === 409 && typeof detail === 'object') {
        // Adopt the stack Interstellar reports so the next click follows it.
        patchState({ stack_name: detail.stack_name });
        Toast.error('El stack ya existe; vuelve a pulsar para seguir su estado.');
      } else {
        Toast.error(
          (typeof detail === 'string' ? detail : detail?.message) ||
            axiosErr?.message ||
            'No se pudo aprovisionar el FreeSWITCH.'
        );
      }
    } finally {
      pollAbort.current = null;
      if (mounted.current) setProvisioning(false);
    }
  };

  const goNext = (): void => {
    if (!canGoNext()) return;
    // Mark current step as completed
    setCompletedSteps((prev) => new Set(prev).add(step));
    const next = steps[currentIdx + 1];
    if (next) setStep(next.key);
  };

  const goBack = (): void => {
    const prev = steps[currentIdx - 1];
    if (prev) setStep(prev.key);
  };

  const handleStepClick = (targetStep: WizardStepKey): void => {
    // Can only click completed steps that are part of the current flow
    if (completedSteps.has(targetStep) && steps.some((s) => s.key === targetStep)) {
      setStep(targetStep);
    }
  };

  const handleCancel = (): void => {
    navigate('/app/phone_settings');
  };

  // Runs before the feature is turned on in Cosmos, so agents never get an
  // empty list of reasons to pick from.
  const createUnavailableReasons = async (accountId: number): Promise<void> => {
    if (!shouldCreateUnavailableReasons(state)) return;
    const failed: string[] = [];
    for (const reason of buildUnavailableReasonsPayload(state.unavailable_reasons)) {
      try {
        await createUnavailableReason(accountId, reason.name, reason.position);
      } catch (err) {
        console.error(`Failed to create unavailable reason ${reason.name}:`, err);
        failed.push(reason.name);
      }
    }
    if (failed.length > 0) {
      Toast.warning(`No se pudieron crear los motivos: ${failed.join(', ')}`);
    } else {
      Toast.success('Motivos de No disponible creados');
    }
  };

  // Runs once the account exists in Interstellar: turning the softphone on any
  // earlier would show telescope a phone with no telephony behind it.
  const saveCosmosPhoneSettings = async (): Promise<void> => {
    try {
      await updateCosmosPhoneSettings(
        state.cosmos_account_id.trim(),
        buildCosmosPhoneSettings(state, hasIvrStep)
      );
      Toast.success('Softphone y funciones activadas en Cosmos');
    } catch {
      Toast.warning(
        'Cuenta creada, pero no se pudieron activar el softphone y sus funciones en Cosmos'
      );
    }
  };

  const handleFinish = async (): Promise<void> => {
    // The sidebar lets the user jump back, edit an earlier step and land on the
    // last step with stale data, so every step in the flow must still be valid.
    if (!steps.every((s) => stepValid(s.key))) {
      Toast.error('Hay pasos incompletos; revisa el wizard antes de crear la cuenta.');
      return;
    }
    setFinishing(true);
    try {
      if (isPrototype) {
        await mockSleep(1000);
        Toast.success('Demostracion de onboarding completada; no se persistieron datos');
      } else {
        if (state.freeswitch_type === 'shared' && state.selected_group) {
          // Shared FreeSWITCH: create account, users, and modules
          const sharedPayload: CreateSharedAccountPayload = {
            name: state.account_name.trim(),
            phone_number: Number(toE164(state.phone_number)),
            call_rescue_number: Number(toE164(state.call_rescue_number)),
            cosmos_account_id: state.cosmos_account_id.trim(),
            freeswitch_group_id: state.selected_group.id,
            admin_username: state.admin_username.trim(),
            admin_password: state.admin_password,
            search_index_name: state.search_index_name.trim() || undefined,
            timezone: state.cosmos_account?.timezone,
            schedule_time_slots: state.schedule.time_slots,
            outbound_carrier: state.outbound_carrier,
          };
          const accountResult = await createSharedAccount(sharedPayload);
          const accountId = accountResult.id;
          Toast.success('Cuenta compartida creada');

          // Track created modules and audio mappings for IVR configuration
          const createdModules: Array<{ id: number; phone_number: string }> = [];
          // Store both id and public_url for IVR configuration
          const audioIdMap = new Map<string, { id: number; public_url?: string }>();

          // Create users if any
          if (state.users.length > 0) {
            const usersPayload: CreateSharedAccountUsersPayload = {
              users: state.users.map((u) => ({
                cosmos_user_id: Number(u.cosmos_user_id),
                phone: Number(u.extension),
                is_admin: u.is_admin,
                in_call_center: u.in_call_center,
              })),
            };
            const usersResult = await createSharedAccountUsers(accountId, usersPayload);
            Toast.success(`${usersResult.created} usuarios creados`);

            // Build a map from wizard user id to real user id
            const userIdMap = new Map<string, number>();
            state.users.forEach((wizUser, idx) => {
              const realUser = usersResult.users[idx];
              if (realUser) {
                userIdMap.set(wizUser.id, realUser.id);
              }
            });

            // Upload audios and create modules if any
            if (state.modules.length > 0 && state.audios.length > 0) {
              // Upload audios: either from file or from default URL
              let uploadedCount = 0;
              let importedCount = 0;

              for (const audio of state.audios) {
                // Map ivr_prompt to 'message' for API compatibility
                const apiType =
                  audio.playback_type === 'ivr_prompt' ? 'message' : audio.playback_type;

                try {
                  if (audio.file) {
                    // Upload local file
                    const result = await createPlaybackAudio(
                      accountId,
                      audio.name,
                      apiType,
                      audio.file
                    );
                    audioIdMap.set(audio.id, { id: result.id, public_url: result.public_url });
                    uploadedCount++;
                  } else if (audio.default_url) {
                    // Import from default S3 URL
                    const result = await importPlaybackFromUrl(
                      accountId,
                      audio.name,
                      apiType,
                      audio.default_url
                    );
                    audioIdMap.set(audio.id, { id: result.id, public_url: result.public_url });
                    importedCount++;
                  }
                } catch (err) {
                  console.error(`Failed to upload/import audio ${audio.name}:`, err);
                  Toast.error(`Error con audio: ${audio.name}`);
                }
              }

              if (uploadedCount > 0 || importedCount > 0) {
                const parts = [];
                if (uploadedCount > 0) parts.push(`${uploadedCount} subidos`);
                if (importedCount > 0) parts.push(`${importedCount} importados`);
                Toast.success(`Audios: ${parts.join(', ')}`);
              }

              // Create resolution strategies
              for (const strategy of state.resolution_strategies) {
                try {
                  const strategyPayload: CreateResolutionStrategyPayload = {
                    name: strategy.name,
                    resolution_time: strategy.resolution_time * 60, // minutes to seconds
                    tags: strategy.tags
                      .filter((t) => t.name.trim())
                      .map((t) => ({
                        name: t.name,
                        temp_id: t.id,
                      })),
                  };
                  await createResolutionStrategy(accountId, strategyPayload);
                  Toast.success(`Estrategia "${strategy.name}" creada`);
                } catch (err) {
                  console.error(`Failed to create resolution strategy ${strategy.name}:`, err);
                  Toast.warning(`Error creando estrategia: ${strategy.name}`);
                }
              }

              // Create modules with uploaded audio IDs
              const scheduleId = accountResult.default_schedule_id;

              for (const module of state.modules) {
                // Get the real user IDs for this module's call group
                // For now, use all users that are in_call_center
                const callGroupUserIds = state.users
                  .filter((u) => u.in_call_center)
                  .map((u) => userIdMap.get(u.id))
                  .filter((id): id is number => id !== undefined);

                if (callGroupUserIds.length === 0) {
                  Toast.warning(`Modulo ${module.name}: sin usuarios para el call group`);
                  continue;
                }

                // Map wizard audio IDs to real IDs
                const greetingData = audioIdMap.get(module.in_office_greeting_id);
                const holdData = audioIdMap.get(module.in_office_hold_id);
                const announceData = audioIdMap.get(module.in_office_announce_id);
                const messageData = audioIdMap.get(module.out_of_office_message_id);

                if (!greetingData?.id || !holdData?.id || !announceData?.id || !messageData?.id) {
                  Toast.warning(
                    `Modulo ${module.name}: faltan audios. Sube todos los audios requeridos.`
                  );
                  continue;
                }

                const modulePayload: CreateSharedModulePayload = {
                  name: module.name,
                  phone_number: module.phone_number,
                  schedule_id: scheduleId,
                  call_group: {
                    name: `Agentes ${module.name}`,
                    user_ids: callGroupUserIds,
                  },
                  in_office: {
                    ring_strategy: module.ring_strategy,
                    greeting_playback_id: greetingData.id,
                    hold_music_playback_id: holdData.id,
                    announce_playback_id: announceData.id,
                    announce_frequency: 30,
                    max_wait_time: module.max_wait_time,
                    max_calls_on_wait: 10,
                  },
                  out_of_office: {
                    message_playback_id: messageData.id,
                  },
                };

                try {
                  const moduleResult = await createSharedModule(accountId, modulePayload);
                  createdModules.push({
                    id: moduleResult.id,
                    phone_number: module.phone_number,
                  });
                  Toast.success(`Modulo ${module.name} creado`);
                } catch (err) {
                  console.error(`Failed to create module ${module.name}:`, err);
                  Toast.error(`Error creando modulo: ${module.name}`);
                }
              }
            }
          }

          // Import Cosmos IVR audios if user chose to use existing IVR
          if (state.use_cosmos_ivr && state.cosmos_ivr) {
            let importedCount = 0;

            // Greeting type number to name mapping
            const greetingTypeNames: Record<number, string> = {
              0: 'Saludo principal',
              1: 'Fuera de horario',
              2: 'Vacaciones',
            };

            // Import greetings
            for (const greeting of state.cosmos_ivr.greetings) {
              if (!greeting.ivr_greeting_audio_file) continue;
              const greetingTypeName =
                greetingTypeNames[greeting.greeting_type] || `Tipo ${greeting.greeting_type}`;
              const name = greetingTypeName;
              try {
                await importPlaybackFromUrl(
                  accountId,
                  name,
                  'ivr_prompt',
                  greeting.ivr_greeting_audio_file
                );
                importedCount++;
              } catch (err) {
                console.error(`Failed to import greeting ${greeting.id}:`, err);
              }
            }

            // Import option audios
            for (const option of state.cosmos_ivr.options) {
              if (!option.audio_message) continue;
              for (const [language, audio] of Object.entries(option.audio_message)) {
                const audioUrl = audio.audio_link || audio.ivr_audio_file;
                if (!audioUrl) continue;
                const name = `IVR ${option.key} - ${option.display_name} (${language})`;
                try {
                  await importPlaybackFromUrl(accountId, name, 'ivr_prompt', audioUrl);
                  importedCount++;
                } catch (err) {
                  console.error(`Failed to import option audio for ${option.key}:`, err);
                }
              }
            }

            if (importedCount > 0) {
              Toast.success(`${importedCount} audios IVR importados de Cosmos`);
            }

            // Configure IVR in FreeSWITCH
            try {
              // Get welcome audio URL from active greeting
              const activeGreeting = state.cosmos_ivr.greetings.find(
                (g) => g.active && g.ivr_greeting_audio_file
              );
              const welcomeAudioUrl = activeGreeting?.ivr_greeting_audio_file || undefined;

              // Build IVR payload
              // All IVR options route to the primary module (same pattern as existing IVRs)
              const primaryModuleId = createdModules[0]?.id;

              const ivrPayload: ConfigureIvrPayload = {
                enabled: true,
                language: state.cosmos_ivr.setting?.language || 'es',
                welcome_audio_url: welcomeAudioUrl,
                timeout_ms: 10000,
                max_failures: 3,
                options: state.cosmos_ivr.options
                  .filter((opt) => opt.is_root && opt.ivr_option_enabled)
                  .map((opt) => {
                    // Map Cosmos audio_message to IvrOptionAudio[]
                    // Normalize language codes: en_US -> en, es_ES -> es
                    const audios: IvrOptionAudio[] | undefined = opt.audio_message
                      ? Object.entries(opt.audio_message).map(([lang, audio]): IvrOptionAudio => {
                          const normalizedLang: 'es' | 'en' = lang.startsWith('es') ? 'es' : 'en';
                          return {
                            language: normalizedLang,
                            selection_audio_url:
                              audio.selection_audio_link ||
                              audio.ivr_selection_audio_file ||
                              undefined,
                            response_audio_url:
                              audio.audio_link || audio.ivr_audio_file || undefined,
                          };
                        })
                      : undefined;

                    return {
                      digit: opt.key,
                      label: opt.display_name,
                      target_module_id: primaryModuleId,
                      audios: audios?.length ? audios : undefined,
                    };
                  }),
                dids: createdModules.map((m) => ({
                  phone_number: m.phone_number,
                  call_module_id: m.id,
                })),
              };

              await configureSharedIvr(accountId, ivrPayload);
              Toast.success('IVR configurado en FreeSWITCH');
            } catch (err) {
              console.error('Failed to configure IVR:', err);
              Toast.warning('IVR importado pero no se pudo configurar en FreeSWITCH');
            }
          }

          // Configure wizard IVR (not from Cosmos) if enabled
          if (
            !state.use_cosmos_ivr &&
            state.ivr.enabled &&
            state.ivr.options.length > 0 &&
            createdModules.length > 0
          ) {
            try {
              // Resolve welcome_audio_url from the uploaded audio
              const welcomeAudioData = audioIdMap.get(state.ivr.welcome_audio_id);
              const welcomeAudioUrl = welcomeAudioData?.public_url;

              if (!welcomeAudioUrl) {
                // Without a welcome audio the IVR is silent: callers hear
                // nothing, hit max_failures and the call is lost. Skip the
                // configuration so the DIDs keep pointing at the module.
                Toast.warning(
                  'No se encontró el audio de bienvenida; el IVR no se configuró en FreeSWITCH'
                );
              } else {
                const primaryModuleId = createdModules[0]?.id;

                const ivrPayload: ConfigureIvrPayload = {
                  enabled: true,
                  language: state.ivr.language,
                  welcome_audio_url: welcomeAudioUrl,
                  timeout_ms: 10000,
                  max_failures: 3,
                  options: buildIvrOptionsPayload(state.ivr.options, {
                    language: state.ivr.language,
                    primaryModuleId,
                    audioUrl: (audioId) => audioIdMap.get(audioId)?.public_url,
                  }),
                  dids: createdModules.map((m) => ({
                    phone_number: m.phone_number,
                    call_module_id: m.id,
                  })),
                };

                await configureSharedIvr(accountId, ivrPayload);
                Toast.success('IVR del wizard configurado en FreeSWITCH');
              }
            } catch (err) {
              console.error('Failed to configure wizard IVR:', err);
              Toast.warning('No se pudo configurar el IVR en FreeSWITCH');
            }
          }

          Toast.success('Configuracion de cuenta compartida completada');
          await createUnavailableReasons(accountId);
          await saveCosmosPhoneSettings();
        } else {
          // Dedicated FreeSWITCH: pass freeswitch_id, uri, and DSN/client_name
          const payload: CreateAccountPayload = {
            name: state.account_name.trim(),
            phone_number: Number(toE164(state.phone_number)),
            call_rescue_number: Number(toE164(state.call_rescue_number)),
            cosmos_account_id: state.cosmos_account_id.trim(),
            // The uuid naming the account's directory in the FreeSWITCH EFS mount,
            // which the provisioned access point creates as /freeswitch/<cosmos uuid>.
            freeswitch_id: state.cosmos_account_id.trim(),
            freeswitch_uri: state.freeswitch_uri.trim(),
            // A provisioned stack is referenced by client name so Interstellar reads
            // the generated password itself; the DSN is only for manual FreeSWITCHes.
            ...(state.provisioned_client_name
              ? { freeswitch_client_name: state.provisioned_client_name }
              : { freeswitch_pg_connection_string: state.pg_connection_string.trim() }),
            admin_username: state.admin_username.trim(),
            admin_password: state.admin_password,
            search_index_name: state.search_index_name.trim(),
            outbound_carrier: state.outbound_carrier,
          };
          const accountResult = await createAccount(payload);
          Toast.success('Cuenta creada exitosamente');
          await createUnavailableReasons(accountResult.id);
          await saveCosmosPhoneSettings();
        }
      }
      navigate('/app/phone_settings');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string; message?: string } } };
      const detail = axiosErr?.response?.data?.detail || axiosErr?.response?.data?.message;
      Toast.error(
        typeof detail === 'string' && detail.length > 0
          ? detail
          : 'Error al crear la cuenta. Verifica los datos e intenta nuevamente.'
      );
    } finally {
      setFinishing(false);
    }
  };

  const handleNext = (): void => {
    if (isLast) {
      void handleFinish();
    } else {
      goNext();
    }
  };

  return (
    <div className="flex h-full overflow-hidden bg-white">
      {/* Sidebar */}
      <WizardSidebar
        steps={steps}
        currentStep={step}
        completedSteps={completedSteps}
        onStepClick={handleStepClick}
      />

      {/* Main content area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Header with prototype warning */}
        {isPrototype && (
          <div className="border-amber-200 bg-amber-50 px-6 py-3 border-b">
            <p className="text-amber-800 text-xs">
              Modo prototipo de Mirage: los pasos posteriores a Cuenta no persisten datos.
            </p>
          </div>
        )}

        {/* Step content */}
        <main className="flex-1 min-h-0 p-6 overflow-y-auto">
          <div className="max-w-3xl mx-auto">
            {step === 'cosmos-search' && <CosmosSearchStep state={state} onChange={patchState} />}
            {step === 'freeswitch-type' && (
              <FreeSwitchTypeStep
                state={state}
                groups={freeswitchGroups}
                loading={loadingGroups}
                onChange={patchState}
              />
            )}
            {step === 'infrastructure' && (
              <InfrastructureStep
                state={state}
                provisioning={provisioning}
                onChange={patchState}
                onProvision={() => void handleProvision()}
                prototype={isPrototype}
              />
            )}
            {step === 'account' && <AccountStep state={state} onChange={patchState} />}
            {step === 'users' && <UsersStep state={state} onChange={patchState} />}
            {step === 'schedule' && (
              <ScheduleStep
                schedule={state.schedule}
                timezone={state.cosmos_account?.timezone}
                onChange={(schedule) => patchState({ schedule })}
              />
            )}
            {step === 'audios' && (
              <AudiosStep
                audios={state.audios}
                onChange={(audios) => patchState({ audios })}
                usages={getAudioUsages(state)}
              />
            )}
            {step === 'resolution-strategies' && (
              <ResolutionStrategyStep state={state} onChange={patchState} />
            )}
            {step === 'modules' && (
              <ModulesStep state={state} onChange={(modules) => patchState({ modules })} />
            )}
            {step === 'ivr' && <IvrStep state={state} onChange={patchState} />}
            {step === 'queues' && (
              <QueuesStep state={state} onChange={(assignments) => patchState({ assignments })} />
            )}
            {step === 'phone-features' && (
              <PhoneFeaturesStep state={state} hasIvrStep={hasIvrStep} onChange={patchState} />
            )}
          </div>
        </main>

        {/* Bottom navigation */}
        <WizardNav
          showBack={currentIdx > 0}
          nextDisabled={!canGoNext() || provisioning}
          isFinalStep={isLast}
          isSubmitting={finishing}
          nextLabel={isPrototype && isLast ? 'Finalizar demostracion' : undefined}
          onBack={goBack}
          onNext={handleNext}
          onCancel={handleCancel}
        />
      </div>
    </div>
  );
};

export default CreateAccountPage;
